import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import type { CandidateDetail, CreateCandidateInput, CandidateSource } from '@pms/shared';
import { departmentsService } from '@/services/departments.service';
import { designationsService } from '@/services/designations.service';
import { recruitmentService } from '@/services/recruitment.service';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SOURCE_OPTIONS, getErrorMessage } from './utils';

const NONE = '__none__';

type Tab = 'personal' | 'professional' | 'education' | 'experience';
const TABS: { key: Tab; label: string }[] = [
  { key: 'personal', label: 'Personal' },
  { key: 'professional', label: 'Professional' },
  { key: 'education', label: 'Education' },
  { key: 'experience', label: 'Experience' },
];

interface EduRow { degree: string; institute: string; year: string; percentage: string }
interface ExpRow { company_name: string; designation: string; start_date: string; end_date: string; is_current: boolean; ctc: string }

const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));
const str = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export function AddEditCandidateModal({
  candidate, onClose, onSaved,
}: {
  candidate?: CandidateDetail;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!candidate;
  const [tab, setTab] = useState<Tab>('personal');
  const [resumeFile, setResumeFile] = useState<File | null>(null);

  const [form, setForm] = useState({
    first_name: candidate?.first_name ?? '',
    last_name: candidate?.last_name ?? '',
    email: candidate?.email ?? '',
    phone: candidate?.phone ?? '',
    alternate_phone: candidate?.alternate_phone ?? '',
    date_of_birth: candidate?.date_of_birth ?? '',
    gender: candidate?.gender ?? '',
    marital_status: candidate?.marital_status ?? '',
    address: candidate?.address ?? '',
    city: candidate?.city ?? '',
    state: candidate?.state ?? '',
    pincode: candidate?.pincode ?? '',
    current_company: candidate?.current_company ?? '',
    current_designation: candidate?.current_designation ?? '',
    total_experience_years: candidate?.total_experience_years ?? '',
    current_ctc: candidate?.current_ctc ?? '',
    expected_ctc: candidate?.expected_ctc ?? '',
    notice_period_days: candidate?.notice_period_days != null ? String(candidate.notice_period_days) : '',
    applied_department_id: candidate?.applied_department_id ?? '',
    applied_designation_id: candidate?.applied_designation_id ?? '',
    source: (candidate?.source ?? 'other') as CandidateSource,
    source_details: candidate?.source_details ?? '',
    notes: candidate?.notes ?? '',
  });
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const [education, setEducation] = useState<EduRow[]>(
    candidate?.education.map((e) => ({
      degree: e.degree, institute: e.institute ?? '', year: e.year ?? '',
      percentage: e.percentage != null ? String(e.percentage) : '',
    })) ?? [],
  );
  const [experience, setExperience] = useState<ExpRow[]>(
    candidate?.experience.map((x) => ({
      company_name: x.company_name, designation: x.designation ?? '',
      start_date: x.start_date ?? '', end_date: x.end_date ?? '',
      is_current: x.is_current, ctc: x.ctc != null ? String(x.ctc) : '',
    })) ?? [],
  );

  const deptQuery = useQuery({ queryKey: ['departments', { limit: 100 }], queryFn: () => departmentsService.list({ limit: 100 }) });
  const desigQuery = useQuery({ queryKey: ['designations', { limit: 100 }], queryFn: () => designationsService.list({ limit: 100 }) });

  const save = useMutation({
    mutationFn: async (): Promise<string> => {
      const payload: CreateCandidateInput = {
        first_name: form.first_name.trim(),
        last_name: str(form.last_name),
        email: form.email.trim(),
        phone: form.phone.trim(),
        alternate_phone: str(form.alternate_phone),
        date_of_birth: str(form.date_of_birth),
        gender: (str(form.gender) as CreateCandidateInput['gender']) ?? null,
        marital_status: str(form.marital_status),
        address: str(form.address),
        city: str(form.city),
        state: str(form.state),
        pincode: str(form.pincode),
        current_company: str(form.current_company),
        current_designation: str(form.current_designation),
        total_experience_years: num(String(form.total_experience_years)),
        current_ctc: num(String(form.current_ctc)),
        expected_ctc: num(String(form.expected_ctc)),
        notice_period_days: num(form.notice_period_days),
        applied_department_id: str(form.applied_department_id),
        applied_designation_id: str(form.applied_designation_id),
        source: form.source,
        source_details: str(form.source_details),
        notes: str(form.notes),
        education: education
          .filter((e) => e.degree.trim())
          .map((e) => ({ degree: e.degree.trim(), institute: str(e.institute), year: str(e.year), percentage: num(e.percentage) })),
        experience: experience
          .filter((x) => x.company_name.trim())
          .map((x) => ({
            company_name: x.company_name.trim(), designation: str(x.designation),
            start_date: str(x.start_date), end_date: str(x.end_date), is_current: x.is_current, ctc: num(x.ctc),
          })),
      };

      let id: string;
      if (isEdit && candidate) {
        await recruitmentService.updateCandidate(candidate.id, payload);
        id = candidate.id;
      } else {
        const res = await recruitmentService.createCandidate(payload);
        id = res.id;
      }
      if (resumeFile) await recruitmentService.uploadResume(id, resumeFile);
      return id;
    },
    onSuccess: (id) => {
      toast.success(isEdit ? 'Candidate updated' : 'Candidate added');
      void queryClient.invalidateQueries({ queryKey: ['recruitment'] });
      onSaved(id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = form.first_name.trim() && form.email.trim() && form.phone.trim();

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Candidate' : 'Add New Candidate'}</DialogTitle>
          <p className="text-sm text-muted-foreground">Enter candidate details for the recruitment pipeline</p>
        </DialogHeader>

        {/* Tab bar */}
        <Tabs tabs={TABS} active={tab} onChange={(k) => setTab(k as Tab)} />

        <div className="max-h-[55vh] overflow-y-auto px-0.5 py-1">
          {tab === 'personal' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="First Name" required><Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} /></Field>
              <Field label="Last Name"><Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} /></Field>
              <Field label="Email" required><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
              <Field label="Phone" required><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
              <Field label="Date of Birth"><DatePicker value={form.date_of_birth} onChange={(v) => set('date_of_birth', v)} /></Field>
              <Field label="Gender">
                <Select value={form.gender || NONE} onValueChange={(v) => set('gender', v === NONE ? '' : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Select</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="City"><Input value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
              <Field label="State"><Input value={form.state} onChange={(e) => set('state', e.target.value)} /></Field>
              <Field label="Pincode"><Input value={form.pincode} onChange={(e) => set('pincode', e.target.value)} /></Field>
              <Field label="Alternate Phone"><Input value={form.alternate_phone} onChange={(e) => set('alternate_phone', e.target.value)} /></Field>
              <div className="sm:col-span-2">
                <Field label="Address"><Textarea rows={2} value={form.address} onChange={(e) => set('address', e.target.value)} /></Field>
              </div>
            </div>
          )}

          {tab === 'professional' && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Current Company"><Input value={form.current_company} onChange={(e) => set('current_company', e.target.value)} /></Field>
              <Field label="Current Designation"><Input value={form.current_designation} onChange={(e) => set('current_designation', e.target.value)} /></Field>
              <Field label="Total Experience (yrs)"><Input type="number" step="0.1" value={form.total_experience_years} onChange={(e) => set('total_experience_years', e.target.value)} /></Field>
              <Field label="Notice Period (days)"><Input type="number" value={form.notice_period_days} onChange={(e) => set('notice_period_days', e.target.value)} /></Field>
              <Field label="Current CTC (₹)"><Input type="number" value={form.current_ctc} onChange={(e) => set('current_ctc', e.target.value)} /></Field>
              <Field label="Expected CTC (₹)"><Input type="number" value={form.expected_ctc} onChange={(e) => set('expected_ctc', e.target.value)} /></Field>
              <Field label="Applied Department">
                <Select value={form.applied_department_id || NONE} onValueChange={(v) => set('applied_department_id', v === NONE ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Select</SelectItem>
                    {(deptQuery.data?.data ?? []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Applied Designation">
                <Select value={form.applied_designation_id || NONE} onValueChange={(v) => set('applied_designation_id', v === NONE ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Select</SelectItem>
                    {(desigQuery.data?.data ?? []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Source" required>
                <Select value={form.source} onValueChange={(v) => set('source', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Source Details"><Input placeholder="e.g. Naukri.com, John Doe" value={form.source_details} onChange={(e) => set('source_details', e.target.value)} /></Field>
              <div className="sm:col-span-2">
                <Field label="Resume (PDF / Word)">
                  <Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setResumeFile(e.target.files?.[0] ?? null)} />
                </Field>
              </div>
            </div>
          )}

          {tab === 'education' && (
            <div className="space-y-3">
              {education.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input placeholder="Degree" value={row.degree} onChange={(e) => setEducation((r) => r.map((x, j) => j === i ? { ...x, degree: e.target.value } : x))} />
                  <Input placeholder="Institute" value={row.institute} onChange={(e) => setEducation((r) => r.map((x, j) => j === i ? { ...x, institute: e.target.value } : x))} />
                  <Input placeholder="Year" className="w-24" value={row.year} onChange={(e) => setEducation((r) => r.map((x, j) => j === i ? { ...x, year: e.target.value } : x))} />
                  <Input placeholder="%" className="w-20" value={row.percentage} onChange={(e) => setEducation((r) => r.map((x, j) => j === i ? { ...x, percentage: e.target.value } : x))} />
                  <button type="button" onClick={() => setEducation((r) => r.filter((_, j) => j !== i))} className="rounded p-1.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setEducation((r) => [...r, { degree: '', institute: '', year: '', percentage: '' }])}>
                <Plus className="h-4 w-4" /> Add Education
              </Button>
            </div>
          )}

          {tab === 'experience' && (
            <div className="space-y-3">
              {experience.map((row, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-border/60 p-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Input placeholder="Company" value={row.company_name} onChange={(e) => setExperience((r) => r.map((x, j) => j === i ? { ...x, company_name: e.target.value } : x))} />
                    <Input placeholder="Designation" value={row.designation} onChange={(e) => setExperience((r) => r.map((x, j) => j === i ? { ...x, designation: e.target.value } : x))} />
                    <DatePicker value={row.start_date} onChange={(v) => setExperience((r) => r.map((x, j) => j === i ? { ...x, start_date: v } : x))} />
                    <div className={row.is_current ? 'pointer-events-none opacity-50' : ''}>
                      <DatePicker value={row.end_date} onChange={(v) => setExperience((r) => r.map((x, j) => j === i ? { ...x, end_date: v } : x))} />
                    </div>
                    <Input type="number" placeholder="CTC (₹)" value={row.ctc} onChange={(e) => setExperience((r) => r.map((x, j) => j === i ? { ...x, ctc: e.target.value } : x))} />
                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                      <input type="checkbox" checked={row.is_current} onChange={(e) => setExperience((r) => r.map((x, j) => j === i ? { ...x, is_current: e.target.checked } : x))} />
                      Currently working here
                    </label>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" onClick={() => setExperience((r) => r.filter((_, j) => j !== i))} className="inline-flex items-center gap-1 rounded p-1 text-xs text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setExperience((r) => [...r, { company_name: '', designation: '', start_date: '', end_date: '', is_current: false, ctc: '' }])}>
                <Plus className="h-4 w-4" /> Add Experience
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" disabled={!canSubmit || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Candidate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}{required && <span className="text-destructive"> *</span>}</Label>
      {children}
    </div>
  );
}
